import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpService } from './http.service';
import { ConfigService } from './config.service';
import { transformSnakeToCamel } from '../../store/utils/transformers';
import { ApiTableFilter } from '../../shared/models/table-filter.model';
import {
  BulkDeleteIpExclusionsPayload,
  IpExclusion,
  IpExclusionSubmitPayload,
  IpExclusionStatistics,
  IpExclusionsListResponse,
  DEFAULT_IP_EXCLUSIONS_CURRENT_PAGE,
  DEFAULT_IP_EXCLUSIONS_PER_PAGE,
} from '../../store/entities/models/ip-exclusion.model';

export interface IpExclusionsListParams {
  page?: number;
  perPage?: number;
  search?: string;
  sort?: string;
  sortDirection?: string;
  filters?: ApiTableFilter[];
  filterLink?: string;
}

/**
 * Result of a create/edit request. When the API rejects a create because the IP
 * already exists it returns the existing exclusion's uuid under `duplicate`.
 */
export interface IpExclusionSubmitResult {
  data?: IpExclusion;
  duplicate?: string;
}

export interface BulkDeleteResult {
  success: boolean;
}

export interface IpExclusionExportPayload {
  selectedRows: string[];
  visibleColumns: Record<string, { headerName: string; index: number }>;
  sort: string;
  sortDirection: string;
  filterLink?: string;
  filters?: ApiTableFilter[];
  allSelected: boolean;
}

export interface IpExclusionExportResult {
  id: string;
}

/**
 * IP Exclusions API. Mirrors the React `_api/admin/ip-exclusions.js` contract:
 * all endpoints live under `/exclusionIps` and are scoped to the active domain by
 * the domain HTTP interceptor (no `/{domainId}` prefix, matching React).
 *
 * The wire format is snake_case; this service converts to/from camelCase at the
 * boundary via the shared transformers.
 */
@Injectable({ providedIn: 'root' })
export class IpExclusionsService {
  private http = inject(HttpService);
  private httpClient = inject(HttpClient);
  private configService = inject(ConfigService);

  private get baseUrl(): string {
    return this.configService.api.host;
  }

  getIpExclusionsList(
    params: IpExclusionsListParams = {}
  ): Observable<IpExclusionsListResponse> {
    const queryParams: Record<string, unknown> = {
      page: params.page ?? DEFAULT_IP_EXCLUSIONS_CURRENT_PAGE,
      per_page: params.perPage ?? DEFAULT_IP_EXCLUSIONS_PER_PAGE,
      sort: params.sort ?? 'created_at',
      sort_direction: params.sortDirection ?? 'desc',
      search: params.search ?? '',
    };

    if (params.filters?.length) {
      queryParams.filters = params.filters;
      queryParams.filter_link = params.filterLink ?? 'and';
    }

    return this.http
      .get<Record<string, unknown>>('/exclusionIps', queryParams)
      .pipe(map((response) => transformSnakeToCamel(response) as IpExclusionsListResponse));
  }

  getStatistics(): Observable<IpExclusionStatistics> {
    return this.http
      .get<{ data: Record<string, unknown> }>('/exclusionIps/statistics')
      .pipe(map((response) => transformSnakeToCamel(response.data ?? {}) as IpExclusionStatistics));
  }

  getIpExclusionById(uuid: string): Observable<IpExclusion> {
    return this.http
      .get<{ data: Record<string, unknown> }>(`/exclusionIps/${uuid}`)
      .pipe(map((response) => transformSnakeToCamel(response.data ?? {}) as IpExclusion));
  }

  createIpExclusion(
    payload: IpExclusionSubmitPayload
  ): Observable<IpExclusionSubmitResult> {
    return this.http
      .post<Record<string, unknown>>(
        '/exclusionIps',
        this.toApiBody(payload)
      )
      .pipe(map((response) => transformSnakeToCamel(response) as IpExclusionSubmitResult));
  }

  updateIpExclusion(
    uuid: string,
    payload: IpExclusionSubmitPayload
  ): Observable<IpExclusionSubmitResult> {
    return this.http
      .post<Record<string, unknown>>(
        `/exclusionIps/${uuid}`,
        this.toApiBody(payload)
      )
      .pipe(map((response) => transformSnakeToCamel(response) as IpExclusionSubmitResult));
  }

  deleteIpExclusion(uuid: string): Observable<void> {
    return this.http.delete<void>(`/exclusionIps/${uuid}`);
  }

  bulkDeleteIpExclusions(
    payload: BulkDeleteIpExclusionsPayload
  ): Observable<BulkDeleteResult> {
    return this.http
      .post<Record<string, unknown>>('/exclusionIps/bulkDelete', {
        exclusion_ip_uuids: payload.exclusionIpUuids,
        all_selected: payload.allSelected,
      })
      .pipe(map((response) => transformSnakeToCamel(response) as BulkDeleteResult));
  }

  exportIpExclusions(
    payload: IpExclusionExportPayload
  ): Observable<IpExclusionExportResult> {
    const body = {
      selected_rows: payload.selectedRows,
      visible_columns: payload.visibleColumns,
      sort: payload.sort,
      sort_direction: payload.sortDirection,
      filter_link: payload.filterLink,
      filters: payload.filters,
      all_selected: payload.allSelected,
    };

    return this.http
      .post<{ data?: { id?: string } }>('/exclusionIps/export', body)
      .pipe(map((response) => ({ id: response?.data?.id ?? '' })));
  }

  /**
   * Uploads a mapped CSV file. Uses HttpClient directly because HttpService does
   * not support multipart/form-data bodies. Auth/domain headers are added by the
   * registered interceptors.
   */
  importCsv(formData: FormData): Observable<unknown> {
    return this.httpClient.post(`${this.baseUrl}/exclusionIps/import`, formData);
  }

  private toApiBody(payload: IpExclusionSubmitPayload): Record<string, unknown> {
    // Explicit mapping (rather than transformCamelToSnake) to match the exact
    // wire keys the API expects: ip_address, domain_uuids, blocked_until.
    return {
      ip_address: payload.ipAddress,
      domain_uuids: payload.domainUuids,
      blocked_until: payload.blockedUntil,
      ...(payload.uuid ? { uuid: payload.uuid } : {}),
    };
  }
}
